import React, { useEffect, useState } from "react";

import { DeploymentService } from "@packages/client";
import { InfoSmIcon as Info, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "ds/Button";
import * as Dialog from "ds/Dialog";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { servicesSelector } from "Reducers/environment/service/service.selectors";
import { setAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import { gitProjectSelector } from "Reducers/project/project";
import Logo from "src/common/containers/NavBar/Logo/Logo";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import * as S from "./ConfigureAutoscalingPage.style";

interface DurationOption {
  label: string;
  value: number;
  id: string;
}

const durationOptions = [
  { label: "1 minute", value: 60, id: "60" },
  { label: "5 minutes", value: 300, id: "300" },
  { label: "10 minutes", value: 600, id: "600" },
  { label: "30 minutes", value: 1800, id: "1800" },
  { label: "60 minutes", value: 3600, id: "3600" }
];

const formatDurations = (duration: number): DurationOption => {
  return {
    label: `${duration / 60} ${duration / 60 > 1 ? "minutes" : "minute"}`,
    value: duration,
    id: duration.toString()
  };
};

const ConfigureAutoscalingPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  //TODO using location state to store service might not be the best approach
  //URL params could be used instead so that state is not lost on refresh
  const { service }: { service: DeploymentService } = location.state;
  const serviceName = service?.name;
  const [isThresholdsEnabled, setEnabledThresholds] = useState<boolean>();
  const [minInstances, setMinInstances] = useState(1);
  const [maxInstances, setMaxInstances] = useState(1);
  const [selectedService, setSelectedService] = useState(service?.name);

  const { canManageAutoscaling } = useAutoscallingValues(service?.name);

  const [thresholdUpperDurationValue, setThresholdUpperDurationValue] =
    useState(durationOptions[1]);

  const [thresholdLowerDurationValue, setThresholdLowerDurationValue] =
    useState(durationOptions[1]);

  const [upperCoolDownDuration, setUpperCoolDownDuration] = useState(
    durationOptions[1]
  );
  const [lowerCoolDownDuration, setLowerCoolDownDuration] = useState(
    durationOptions[1]
  );

  const { projectId, environmentId } = useDecodedParams();

  const autoscalingConfig = useAppSelector(servicesSelector);
  const [thresholdUpperValue, setThresholdUpperValue] = useState<number>(80);
  const [thresholdLowerValue, setThresholdLowerValue] = useState<number>(20);
  const project = useSelectorWithUrlParams(gitProjectSelector);

  useEffect(() => {
    if (serviceName) {
      setSelectedService(serviceName);
    }
    if (
      !autoscalingConfig.autoscalingLoading &&
      autoscalingConfig.autoscalingConfiguration
    ) {
      const isEnabled =
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.enabled;
      const upperDuration =
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.triggers
          ?.cpu?.up?.duration;
      const lowerDuration =
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.triggers
          ?.cpu?.down?.duration;
      if (upperDuration) {
        const formattedUpperDuration = formatDurations(upperDuration);
        setThresholdUpperDurationValue(formattedUpperDuration);
      }
      if (lowerDuration) {
        const formattedLowerDuration = formatDurations(lowerDuration);

        setThresholdLowerDurationValue(formattedLowerDuration);
      }

      setMinInstances(
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.instances
          ?.min
      );
      setMaxInstances(
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.instances
          ?.max
      );
      const lowerCoolDownDuration =
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]
          ?.scale_cooldown?.down;
      const upperCoolDownDuration =
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]
          ?.scale_cooldown?.up;

      if (upperCoolDownDuration) {
        const formattedUpperCoolDownDuration = formatDurations(
          upperCoolDownDuration
        );
        setUpperCoolDownDuration(formattedUpperCoolDownDuration);
      }

      if (lowerCoolDownDuration) {
        const formattedLowerCoolDownDuration = formatDurations(
          lowerCoolDownDuration
        );
        setLowerCoolDownDuration(formattedLowerCoolDownDuration);
      }

      setThresholdUpperValue(
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.triggers
          ?.cpu?.up?.threshold
      );
      setThresholdLowerValue(
        autoscalingConfig?.autoscalingConfiguration?.[serviceName]?.triggers
          ?.cpu.down?.threshold
      );

      if (isEnabled !== undefined) {
        setEnabledThresholds(isEnabled);
      }
    }
  }, [autoscalingConfig, serviceName]);

  const handleClosePage = () => {
    navigate(-1);
  };
  const handleConfigureThresholds = async () => {
    const settings = {
      services: {
        [selectedService]: {
          enabled: isThresholdsEnabled ?? false,
          triggers: {
            cpu: {
              up: {
                threshold: Number(
                  thresholdUpperValue ? thresholdUpperValue : 80
                ),
                duration: thresholdUpperDurationValue.value
              },
              down: {
                threshold: Number(
                  thresholdLowerValue ? thresholdLowerValue : 20
                ),
                duration: thresholdLowerDurationValue.value
              }
            }
          },
          scale_cooldown: {
            up: upperCoolDownDuration.value,
            down: lowerCoolDownDuration.value
          },
          instances: {
            min: Number(minInstances),
            max: Number(maxInstances)
          }
        }
      }
    };
    await dispatch(
      setAutoscalingConfigurationThunk({ projectId, environmentId, settings })
    ).unwrap();

    navigate(-1);
  };

  return (
    <S.ConfigureAutoscalingWrapper>
      <S.VendorIconWrapper>
        <Button to="/">
          <Logo isFullLogo />
        </Button>
      </S.VendorIconWrapper>
      <S.DescriptionWrapper>
        <Typography.Label>{`${project?.title ?? ""} >  ${environmentId}`}</Typography.Label>

        <Typography.Title tag="h1">
          {intl.formatMessage({
            id: "configure.alarm.thresholds"
          })}
        </Typography.Title>
        <Typography.BodyText>
          {intl.formatMessage(
            {
              id: "define.thresholds.modal"
            },
            {
              br: () => <br />
            }
          )}
        </Typography.BodyText>

        {!!autoscalingConfig.autoscalingError && (
          <S.StickerError priority="critical" variant="short">
            <Sticker.Icon priority="critical" />
            <Sticker.Text>
              {intl.formatMessage({
                id: "autoscaling.generic.error.message"
              })}
            </Sticker.Text>
          </S.StickerError>
        )}

        {!canManageAutoscaling && (
          <S.StickerPermissions priority="information">
            <Sticker.Icon priority="information" />
            <Sticker.Text>
              {intl.formatMessage({
                id: "autoscaling.permission.request"
              })}
            </Sticker.Text>
          </S.StickerPermissions>
        )}
      </S.DescriptionWrapper>
      <S.ContentWrapper>
        <S.AppsAndResources>
          <S.SelectedAppContainer>
            <S.ApplicationLabel size="extraSmall" weight="semiBold">
              Application
            </S.ApplicationLabel>
            <Typography.BodyText weight="bold">
              {selectedService}
            </Typography.BodyText>
          </S.SelectedAppContainer>
          <S.InstancesWrapper>
            <S.MinimumInstancesWrapper>
              <Typography.Label>
                {intl.formatMessage({
                  id: "min.instances.dialog.title"
                })}
              </Typography.Label>
              <S.MinInstancesDialog>
                <Dialog.Trigger>
                  <Info color="var(--mode-vector-neutral-default)" />
                </Dialog.Trigger>

                <S.MinContent>
                  <Typography.BodyText size="small" weight="semiBold">
                    {intl.formatMessage({
                      id: "min.instances.dialog.title"
                    })}
                  </Typography.BodyText>
                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "min.instances.dialog.description"
                    })}
                  </Typography.BodyText>
                </S.MinContent>
              </S.MinInstancesDialog>

              <S.MinimumInstances
                id="min-instances"
                value={minInstances}
                onChange={e =>
                  setMinInstances(e.target.value as unknown as number)
                }
                disabled={!canManageAutoscaling}
              />
            </S.MinimumInstancesWrapper>
            <S.MaximumInstancesWrapper>
              <Typography.Label>
                {intl.formatMessage({
                  id: "max.instances.dialog.title"
                })}
              </Typography.Label>
              <S.MaxInstancesDialog>
                <Dialog.Trigger>
                  <Info color="var(--mode-vector-neutral-default)" />
                </Dialog.Trigger>
                <S.MaxContent>
                  <Typography.BodyText size="small" weight="semiBold">
                    {intl.formatMessage({
                      id: "max.instances.dialog.title"
                    })}
                  </Typography.BodyText>
                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "max.instances.dialog.description"
                    })}
                  </Typography.BodyText>
                </S.MaxContent>
              </S.MaxInstancesDialog>

              <S.MaximumInstances
                id="max-instances"
                value={maxInstances}
                onChange={e =>
                  setMaxInstances(e.target.value as unknown as number)
                }
                disabled={!canManageAutoscaling}
              />
            </S.MaximumInstancesWrapper>
          </S.InstancesWrapper>
        </S.AppsAndResources>
        <hr />

        <S.EnableThresholds>
          <S.EnableCheckbox
            checked={!!isThresholdsEnabled}
            onCheckedChanged={() => setEnabledThresholds(!isThresholdsEnabled)}
            label="Enable CPU triggers"
            forId="enable-thresholds"
            disabled={!canManageAutoscaling}
          />
        </S.EnableThresholds>
        <S.ThresholdsValues>
          <S.UpperThreshold>
            <Typography.BodyText>
              {intl.formatMessage(
                {
                  id: "add.n.instances"
                },
                {
                  strong: (content: React.ReactNode) => (
                    <S.BoldText weight="semiBold">{content}</S.BoldText>
                  ),
                  metric: "CPU"
                }
              )}
            </Typography.BodyText>
            <S.ValuesWrapper>
              <S.ThresholdInput
                id="upper-threshold"
                placeholder="80"
                value={thresholdUpperValue}
                onChange={e =>
                  setThresholdUpperValue(e.target.value as unknown as number)
                }
                disabled={!canManageAutoscaling}
              />
              <Typography.BodyText>
                {intl.formatMessage({
                  id: "duration.of"
                })}
              </Typography.BodyText>
              <S.DurationOptions
                options={durationOptions}
                defaultValue={durationOptions[1]}
                value={thresholdUpperDurationValue}
                onChange={value =>
                  setThresholdUpperDurationValue(value as DurationOption)
                }
                isDisabled={!canManageAutoscaling}
              />

              <Typography.BodyText>
                {intl.formatMessage({
                  id: "then.wait"
                })}
              </Typography.BodyText>

              <S.CoolDownDurationOptions
                options={durationOptions}
                defaultValue={durationOptions[1]}
                value={upperCoolDownDuration}
                onChange={value =>
                  setUpperCoolDownDuration(value as DurationOption)
                }
                isDisabled={!canManageAutoscaling}
              />
              <Typography.BodyText>
                {intl.formatMessage({
                  id: "before.adding.more"
                })}
              </Typography.BodyText>
            </S.ValuesWrapper>
          </S.UpperThreshold>
          <S.LowerThreshold>
            <Typography.BodyText>
              {intl.formatMessage(
                {
                  id: "remove.n.instances"
                },
                {
                  strong: (content: React.ReactNode) => (
                    <S.BoldText weight="semiBold">{content}</S.BoldText>
                  ),
                  metric: "CPU"
                }
              )}
            </Typography.BodyText>
            <S.ValuesWrapper>
              <S.ThresholdInput
                placeholder="20"
                value={thresholdLowerValue}
                onChange={e =>
                  setThresholdLowerValue(e.target.value as unknown as number)
                }
                disabled={!canManageAutoscaling}
              />
              <Typography.BodyText>
                {intl.formatMessage({
                  id: "duration.of"
                })}
              </Typography.BodyText>
              <S.DurationOptions
                options={durationOptions}
                value={thresholdLowerDurationValue}
                defaultValue={durationOptions[1]}
                onChange={value =>
                  setThresholdLowerDurationValue(value as DurationOption)
                }
                isDisabled={!canManageAutoscaling}
              />
              <Typography.BodyText>
                {intl.formatMessage({
                  id: "then.wait"
                })}
              </Typography.BodyText>

              <S.CoolDownDurationOptions
                options={durationOptions}
                defaultValue={durationOptions[1]}
                value={lowerCoolDownDuration}
                onChange={value =>
                  setLowerCoolDownDuration(value as DurationOption)
                }
                isDisabled={!canManageAutoscaling}
              />
              <Typography.BodyText>
                {intl.formatMessage({
                  id: "before.removing.more"
                })}
              </Typography.BodyText>
            </S.ValuesWrapper>
          </S.LowerThreshold>
        </S.ThresholdsValues>
        <hr />

        <S.ActionButtons>
          <Button variant="secondary" onClick={() => handleClosePage()}>
            Cancel
          </Button>
          <Button
            disabled={!canManageAutoscaling}
            onClick={() => handleConfigureThresholds()}
          >
            Save
          </Button>
        </S.ActionButtons>
      </S.ContentWrapper>
    </S.ConfigureAutoscalingWrapper>
  );
};

export default ConfigureAutoscalingPage;
